import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import Request from "../common/Request.jsx";
import { RegistrationTracker } from "./RegistrationTracker";
import {
  OnboardingFooter,
  OnboardingHeader,
  REGISTRATION_STEPS,
} from "./onboardingChrome";
import { ArrowLeftIcon, ArrowRightIcon } from "./onboardingIcons";
import {
  ANNUAL_SALES,
  CAREER_STAGES,
  CareerStageCode,
  DEFAULT_SALES_BY_STAGE,
  ONBOARDING_PROFILE_CONTINUED,
  ONBOARDING_PROFILE_VIEWED,
  ProfileEventName,
  RevenueBandCode,
  validateName,
} from "./profileOptions";

const SUBHEADING =
  "A few quick details about your practice. We'll use them to set up your account and personalise what you see first.";

const STUDIO_DESCRIPTION =
  "Your public-facing brand name. If you trade under your own name, this can match your full name.";

const PHONE_DESCRIPTION =
  "So our team can reach you if we need to help with your setup.";

const CONTROL_HEIGHT = 48;

const fieldStyles = {
  input: { height: CONTROL_HEIGHT, fontSize: 15 },
  label: { fontWeight: 400, fontSize: 14, color: "var(--mantine-color-dark-7)" },
  description: { marginBottom: 8 },
};

function resolveEventsUrl(): string {
  const resolved =
    typeof window !== "undefined" &&
    (
      window as unknown as {
        Artcloud?: {
          url?: {
            vendor?: { onboarding?: { events?: { post?: () => string } } };
          };
        };
      }
    ).Artcloud?.url?.vendor?.onboarding?.events?.post?.();
  return resolved || "/api/onboarding/events";
}

interface BeaconExtras {
  CareerStage?: string;
  RevenueBand?: string;
}

function emitOnboardingEvent(
  eventName: ProfileEventName,
  isArtistReferral: boolean,
  extras: BeaconExtras = {},
): void {
  // Fire-and-forget: a rejected beacon promise must be caught here or it
  // becomes an unhandled rejection; the try/catch only guards the sync path.
  try {
    const pending = Request.post(resolveEventsUrl(), {
      EventName: eventName,
      IsArtistReferral: isArtistReferral,
      ...extras,
    });
    if (pending && typeof pending.catch === "function") {
      pending.catch(() => {
        /* swallow — analytics is best-effort */
      });
    }
  } catch (_) {
    /* swallow synchronous failures */
  }
}

export interface OnboardingProfileValues {
  fullName: string;
  studioName: string;
  phone: string;
  careerStage: CareerStageCode | "";
  annualSales: RevenueBandCode | "";
}

export interface OnboardingProfileScreenProps {
  isArtistReferral: boolean;
  signedInAs?: string;
  initialValues?: Partial<OnboardingProfileValues>;
  onContinue: (values: OnboardingProfileValues) => void | Promise<void>;
  onBack: (values: OnboardingProfileValues) => void;
}

function renderCareerStageOption({ option }: { option: { value: string; label: string } }) {
  const stage = CAREER_STAGES.find((s) => s.value === option.value);
  return (
    <Box>
      <Text size="sm">{option.label}</Text>
      {stage ? (
        <Text size="xs" c="gray.6">
          {stage.subLabel}
        </Text>
      ) : null}
    </Box>
  );
}

export function OnboardingProfileScreen({
  isArtistReferral,
  signedInAs,
  initialValues = {},
  onContinue,
  onBack,
}: OnboardingProfileScreenProps) {
  const form = useForm<OnboardingProfileValues>({
    initialValues: {
      fullName: initialValues.fullName ?? "",
      studioName: initialValues.studioName ?? "",
      phone: initialValues.phone ?? "",
      careerStage: initialValues.careerStage ?? "",
      annualSales: initialValues.annualSales ?? "",
    },
    validate: {
      fullName: (value) => {
        const result = validateName(value);
        return result.ok ? null : result.msg;
      },
      studioName: (value) => {
        const result = validateName(value);
        return result.ok ? null : result.msg;
      },
    },
    validateInputOnBlur: ["fullName", "studioName"],
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    emitOnboardingEvent(ONBOARDING_PROFILE_VIEWED, isArtistReferral);
  }, [isArtistReferral]);

  const values = form.values;
  const canSubmit =
    validateName(values.fullName).ok &&
    validateName(values.studioName).ok &&
    !!values.careerStage;

  function handleCareerStageChange(value: string | null) {
    const stage = (value ?? "") as CareerStageCode | "";
    form.setFieldValue("careerStage", stage);
    if (stage) {
      form.setFieldValue("annualSales", DEFAULT_SALES_BY_STAGE[stage]);
    }
  }

  async function handleContinue(submitted: OnboardingProfileValues) {
    emitOnboardingEvent(ONBOARDING_PROFILE_CONTINUED, isArtistReferral, {
      CareerStage: submitted.careerStage,
      RevenueBand: submitted.annualSales,
    });
    setSubmitError(null);
    setSubmitting(true);
    try {
      // On success the parent advances and unmounts; only reset on failure.
      await Promise.resolve(onContinue(submitted));
    } catch (_) {
      setSubmitError(
        "We couldn't save your details. Please check your connection and try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--mantine-color-gray-0)",
      }}
    >
      <OnboardingHeader
        signedInAs={signedInAs}
        logoTestId="brand-logo"
        signedInTestId="signed-in-as"
      />

      <Box style={{ padding: "0.5rem 1.5rem", maxWidth: 760, margin: "0 auto", width: "100%" }}>
        <RegistrationTracker steps={REGISTRATION_STEPS} activeStep={2} />
      </Box>

      <Box
        component="main"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "3rem 1.5rem 1.5rem",
        }}
      >
        <Paper
          radius="lg"
          shadow="sm"
          p={40}
          maw={620}
          w="100%"
          data-testid="profile-card"
          style={{ border: "1px solid var(--mantine-color-gray-2)" }}
        >
          <Stack gap="md">
            <Box>
              <Title
                order={1}
                fz="30px"
                fw={500}
                lh={1.2}
                data-testid="screen-heading"
                style={{ letterSpacing: "-0.01em", margin: 0 }}
              >
                Tell us about yourself
              </Title>
              <Text size="sm" c="gray.6" mt={6} data-testid="screen-subheading">
                {SUBHEADING}
              </Text>
            </Box>

            <form
              aria-label="Tell us about yourself"
              onSubmit={form.onSubmit(handleContinue)}
              noValidate
            >
              <Stack gap="md">
                <TextInput
                  label="Your full name"
                  autoComplete="name"
                  required
                  maxLength={100}
                  data-testid="full-name-input"
                  styles={fieldStyles}
                  {...form.getInputProps("fullName")}
                />

                <TextInput
                  label="Studio name"
                  description={STUDIO_DESCRIPTION}
                  placeholder="e.g. Blue Door Studio"
                  required
                  maxLength={100}
                  data-testid="studio-name-input"
                  styles={fieldStyles}
                  {...form.getInputProps("studioName")}
                />

                <TextInput
                  label={
                    <>
                      Phone{" "}
                      <Text span c="gray.5" fw={400} fz={14}>
                        (optional)
                      </Text>
                    </>
                  }
                  type="tel"
                  description={PHONE_DESCRIPTION}
                  placeholder="+44 20 7946 0958"
                  data-testid="phone-input"
                  styles={fieldStyles}
                  {...form.getInputProps("phone")}
                />

                <Box className="onboarding-profile-fields">
                  <Select
                    label="Career stage"
                    required
                    withAsterisk
                    allowDeselect={false}
                    placeholder="Select stage"
                    data-testid="career-stage-select"
                    data={CAREER_STAGES.map((s) => ({ value: s.value, label: s.label }))}
                    renderOption={renderCareerStageOption}
                    value={values.careerStage || null}
                    onChange={handleCareerStageChange}
                    comboboxProps={{ withinPortal: false }}
                    styles={fieldStyles}
                  />

                  <Select
                    label="Annual sales"
                    clearable
                    clearButtonProps={{ "aria-label": "Clear annual sales" }}
                    placeholder="Select range"
                    data-testid="annual-sales-select"
                    data={ANNUAL_SALES.map((o) => ({ value: o.value, label: o.label }))}
                    value={values.annualSales || null}
                    onChange={(value) =>
                      form.setFieldValue(
                        "annualSales",
                        (value ?? "") as RevenueBandCode | "",
                      )
                    }
                    comboboxProps={{ withinPortal: false }}
                    styles={fieldStyles}
                  />
                </Box>

                {submitError ? (
                  <Text size="sm" c="red.7" data-testid="profile-submit-error">
                    {submitError}
                  </Text>
                ) : null}

                <Group
                  className="onboarding-profile-actions"
                  justify="space-between"
                  mt="lg"
                  wrap="nowrap"
                >
                  <Button
                    type="button"
                    variant="default"
                    h={CONTROL_HEIGHT}
                    px="md"
                    fw={500}
                    disabled={submitting}
                    onClick={() => onBack(form.getValues())}
                    data-testid="back-button"
                    aria-label="Back"
                  >
                    <ArrowLeftIcon />
                  </Button>
                  <Button
                    type="submit"
                    className="onboarding-profile-continue"
                    h={CONTROL_HEIGHT}
                    px="xl"
                    fw={500}
                    disabled={!canSubmit || submitting}
                    loading={submitting}
                    rightSection={<ArrowRightIcon />}
                    data-testid="continue-button"
                  >
                    Continue
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Box>

      <OnboardingFooter />
    </Box>
  );
}
